/* global ifCl */
import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import ActionsContext from "common/ActionsContext";
import Request from "common/Request";
import { TitleWithNav } from "common/TitleWithNav";
import Help from "./Help";
import SelectInputWithIcon from "common/SelectInputWithIcon";
import {
  getApplicationMetricsMock,
  parseApplicationMetrics,
  parsePolicyNames,
  parseSubscriberGroupsNames,
  getPolicyFilterCmd,
  getSubscriberGroupFilterCmd,
} from "./api";
import SelectMultiAutocomplete from "common/SelectMultiAutocomplete";
import { safeStr } from "common/api";
import Switch from "@mui/material/Switch";
import TimeCharts from "./TimeCharts";
import BoxplotMultiple from "common/BoxplotMultiple";
import LineChartOrdinal from "common/graphs/LineChartOrdinal";

const ChartTitle = styled.h4`
  margin-top: 0;
  margin-bottom: 0;
`;

const LayoutRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  @media (max-width: 750px) {
    flex-direction: column;
    align-items: flex-end;
    flex-wrap: wrap;

    .displaySelect {
      margin-right: 30px;
    }
  }
`;

const FiltersRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  @media (max-width: 800px) {
    flex-wrap: wrap;
    margin-top: 0px;
  }
`;

const FiltersLeft = styled.div`
  display: flex;
  & > div {
    margin-right: 10px;
  }

  @media (max-width: 800px) {
    width: 100%;
  }
`;

const Wrapper = styled.div`
  .sw-input.MuiSwitch-root {
    & .Mui-checked {
      color: var(--switch-checked-green);
    }

    & .Mui-checked + .MuiSwitch-track {
      background-color: var(--switch-checked-light-green);
    }
  }
`;

const SwitchWithLabel = styled.div`
  display: flex;
  align-items: center;
  position: relative;
`;

const Label = styled.div`
  margin-right: 10px;
  font-family: AllianceNo2-SemiBold !important;
  font-weight: 100;
  color: var(--Text-Primary);
  width: 100px;
  text-align: right;
`;

const MultiSelectorsWrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  & > div {
    width: 50%;
    margin: 0 10px;
  }
  @media (max-width: 800px) {
    flex-direction: column;
    & > div {
      width: 100%;
      margin-right: 0px;
    }
  }
`;

const SelectWrapper = styled.div`
  width: 250px;
`;

const SwitchWrapper = styled.div`
  width: auto;
  display: flex;
  align-items: center;
  position: relative;
`;

const _displayChoices = [
  { value: "updown", label: "Downlink & Uplink Speed per Flow" },
  { value: "downall", label: "Downlink Speed & Time Evolution" },
  { value: "upall", label: "Uplink Speed & Time Evolution" },
];

const ContextMenu = () => {
  const actions = useContext(ActionsContext);
  return (
    <ul className="context-menu header-dropdown m-r--5">
      <a
        data-loading-effect="pulse"
        data-toggle="cardloading"
        title="Help"
        onClick={() => actions.send("open-help")}
      >
        <i className="material-icons">help_outline</i>
      </a>
      <a
        onClick={() => actions.send("do-load")}
        data-toggle="cardloading"
        data-loading-effect="pulse"
        title="Refresh"
      >
        <i className="material-icons">refresh</i>
      </a>
    </ul>
  );
};

export function AppCharts({ data, plotMode, info, display, showMinMax, className }) {
  const { downlink, uplink } = data;
  const appsDownlink = downlink.map((item) => item.app);
  const appsUplink = uplink.map((item) => item.app);
  const [filteredAppsDown, setFilteredAppsDown] = useState(
    new Set(appsDownlink)
  );
  const [filteredAppsUp, setFilteredAppsUp] = useState(new Set(appsUplink));

  return (
    <div className={`margin-t-20 ${className ? className : ""}`}>
      {display !== "upall" ? (
        <div>
          <ChartTitle>{info.titleDown}</ChartTitle>
          {plotMode === "boxplot" ? (
            <BoxplotMultiple
              data={data.downlink}
              selection={filteredAppsDown}
              setSelection={setFilteredAppsDown}
              minmax={showMinMax}
            />
          ) : (
            <LineChartOrdinal
              data={data.downlink}
              selection={filteredAppsDown}
              setSelection={setFilteredAppsDown}
              minmax={showMinMax}
            />
          )}
        </div>
      ) : null}
      {display !== "downall" ? (
        <div className="margin-t-0">
          <ChartTitle>{info.titleUp}</ChartTitle>
          {plotMode === "boxplot" ? (
            <BoxplotMultiple
              data={data.uplink}
              selection={filteredAppsUp}
              setSelection={setFilteredAppsUp}
              minmax={showMinMax}
            />
          ) : (
            <LineChartOrdinal
              data={data.uplink}
              selection={filteredAppsUp}
              setSelection={setFilteredAppsUp}
              minmax={showMinMax}
            />
          )}
        </div>
      ) : null}
    </div>
  );
}

const ApplicationMetrics = ({ returnView = null, test = false }) => {
  const [hours, setHours] = useState(24);
  const [request, setRequest] = useState(null);
  const [policiesRate, setPoliciesRate] = useState([]);
  const [policiesRateSelected, setPoliciesRateSelected] = useState([]);
  const [subscriberGroups, setSubscriberGroups] = useState([]);
  const [subscriberGroupSelected, setSubscriberGroupSelected] = useState([]);
  const [plotMode, setPlotMode] = useState("boxplot");
  const [display, setDisplay] = useState("downall");
  const [showMinMax, setShowMinMax] = useState(false);
  const actions = useContext(ActionsContext);

  useEffect(() => {
    doLoad();

    return actions.recv("do-load", doLoad);
  }, [
    hours,
    JSON.stringify(policiesRateSelected),
    JSON.stringify(subscriberGroupSelected),
  ]);

  useEffect(() => {
    doLoadFilters();
  }, []);

  function doLoadFilters() {
    Promise.all([
      ifCl.run("show policy rate"),
      ifCl.run("show api subscriber-group"),
    ]).then(([policiesResp, subscriberGroupsResp]) => {
      setPoliciesRate(parsePolicyNames(policiesResp));
      setSubscriberGroups(parseSubscriberGroupsNames(subscriberGroupsResp));
    });
  }

  function doLoad() {
    const promise = (
      test
        ? Promise.resolve(getApplicationMetricsMock())
        : ifCl.run(
            `show pkteng app-metrics hours ${hours} categories 19 subscriber all  policy ${getPolicyFilterCmd(
              policiesRateSelected
            )} subscriber-group ${getSubscriberGroupFilterCmd(
              subscriberGroupSelected
            )} percentile`, { timeout: DPI_STATS_TMOUT_MS }
          )
    )
      .then(parseApplicationMetrics)
      .catch((error) => {
        showModalError("Error", error);
      });

    setRequest(promise);
  }

  return (
    <Wrapper>
      <div className="col-lg-12 col-md-12 col-sm-12 col-xs-12">
        <div className="card">
          <div className="header block-header">
            <TitleWithNav returnView={returnView}>
              <h2 className="title">Application Speed Metrics</h2>
            </TitleWithNav>
            <ContextMenu />
          </div>
          <div className="body flex-panel-column">
            <LayoutRow>
              <SelectWrapper className="displaySelect">
                <SelectInputWithIcon
                  title="Display"
                  name="Display"
                  icon="apps"
                  selected={display}
                  onChange={({ target }) => setDisplay(target.value)}
                  options={_displayChoices}
                />
              </SelectWrapper>
              <SwitchWrapper>
                <SwitchWithLabel>
                  <Label>Line chart view</Label>{" "}
                  <Switch
                    className="sw-input"
                    checked={plotMode !== "boxplot"}
                    onChange={() =>
                      setPlotMode(plotMode === "boxplot" ? "line" : "boxplot")
                    }
                  />
                </SwitchWithLabel>
                <SwitchWithLabel>
                  <Label>View max+min</Label>{" "}
                  <Switch
                    className="sw-input"
                    checked={showMinMax}
                    onChange={() => setShowMinMax(!showMinMax)}
                  />
                </SwitchWithLabel>
              </SwitchWrapper>
            </LayoutRow>
            <FiltersRow>
              <FiltersLeft>
                <SelectInputWithIcon
                  title="Date Range"
                  name="hours"
                  icon="date_range"
                  selected={hours || 24}
                  onChange={({ target }) => setHours(parseInt(target.value))}
                  options={[
                    { value: 24, label: "1 Day" },
                    { value: 24 * 7, label: "1 Week" },
                    { value: 24 * 30, label: "1 Month" },
                    { value: 24 * 30 * 3, label: "3 Months" },
                  ]}
                />
              </FiltersLeft>
              <MultiSelectorsWrapper>
                <SelectMultiAutocomplete
                  icon="folder_open"
                  title="Subscriber Group"
                  name="subscriber-group-selector"
                  placeholder="Subscriber Group"
                  options={subscriberGroups.map((name) => {
                    return { label: name, value: name };
                  })}
                  key="subscriberGroupSelector"
                  onChange={(value) =>
                    setSubscriberGroupSelected(
                      value.map((optionType) => optionType.value)
                    )
                  }
                />
                <SelectMultiAutocomplete
                  icon="folder_open"
                  title="Rate policy"
                  name="policy-rate-selector"
                  placeholder="Rate policy"
                  options={policiesRate.map((name) => {
                    return { label: name, value: name };
                  })}
                  key="policyRateSelector"
                  onChange={(value) =>
                    setPoliciesRateSelected(
                      value.map((optionType) => optionType.value)
                    )
                  }
                />
              </MultiSelectorsWrapper>
            </FiltersRow>
            <Request during={request}>
              {(result) => {
                return (
                  <>
                    <AppCharts
                      data={result}
                      plotMode={plotMode}
                      info={{
                        titleDown: "Downlink speed per flow",
                        titleUp: "Uplink speed per flow",
                      }}
                      display={display}
                      showMinMax={showMinMax}
                    />
                    {display !== "updown" && (
                      <TimeCharts
                        data={result}
                        plotMode={plotMode}
                        hours={hours}
                        subscriberGroups={subscriberGroupSelected}
                        policiesRate={policiesRateSelected}
                        display={display}
                        showMinMax={showMinMax}
                      />
                    )}
                  </>
                );
              }}
            </Request>
          </div>
          <Help />
        </div>
      </div>
    </Wrapper>
  );
};

export default ApplicationMetrics;
